import React, { useState, useEffect } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import DoctorTable from "./DoctorTable";
import DoctorModal from "./Add Doctor/DoctorModal";
import Pagination from "./Pagination";
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../Components/PageHeader";

function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rowsPerPage = 10;

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const { data } = await axiosInstance.get("/doctors/details");
        if (Array.isArray(data)) {
          const enriched = data.map((doctor) => ({
            ...doctor,
            name: doctor.name || "--",
            department: doctor.departmentName || "Unknown",
            status: doctor.status || "inactive",
          }));
          setDoctors(enriched);
        } else {
          console.error("Unexpected doctors format:", data);
          setDoctors([]);
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  const filteredDoctors = doctors.filter(
    (doctor) =>
      (doctor.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (doctor.department?.toLowerCase() || "").includes(
        searchQuery.toLowerCase()
      )
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentDoctors = filteredDoctors.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(filteredDoctors.length / rowsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const updateDoctorStatus = (doctorId, newStatus) => {
    setDoctors((prevDoctors) =>
      prevDoctors.map((doctor) =>
        doctor.id === doctorId ? { ...doctor, status: newStatus } : doctor
      )
    );
  };

  const handleDoctorAdded = (newDoctor) => {
    setDoctors((prev) => [newDoctor, ...prev]);
  };

  if (loading) {
    return (
      <div className="flex h-screen justify-center items-center bg-gray-100">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Page Header */}
      <PageHeader
        title="Doctors"
        subtitle="Manage doctors and their schedules"
        showBackButton={true}
        action={
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition"
          >
            Add Doctor
          </button>
        }
      />

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative w-full sm:w-96">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search doctors by name or department..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Doctor Table */}
      <div className="overflow-x-auto">
        <DoctorTable
          doctors={currentDoctors}
          onStatusChange={updateDoctorStatus}
        />
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Doctor Modal */}
      <DoctorModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        title="Add Doctor"
        submitButtonText="Add"
        onDoctorAdded={handleDoctorAdded}
      />
    </div>
  );
}

export default Doctors;
